import React, { useState, useCallback, useRef } from 'react';
import { ControlLink, Stack, Text } from '@moda/om';
import { omit } from 'ramda';
import { useHistory } from 'react-router-dom';
import WarningIcon from '@moda/icons/warning-20';
import { Box } from '../../Box';
import { PaymentSource } from '../../../constants';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { Mode as AdyenPaymentMethodSelectorMode } from '../../PaymentMethod/AdyenPaymentMethodSelector';
import { ApplePaymentMethodPreview } from '../../AppleShippingAndPayment';
import { PayPalPaymentMethodPreview } from '../../PayPalShippingAndPayment/PayPalPaymentMethodPreview';
import { AffirmPaymentMethodPreview } from '../../AffirmShippingAndPayment/AffirmPaymentMethodPreview';
import { useOrderDetails } from '../../OrderDetails';
import { OrderDetailsAffirmPaymentMethod } from './OrderDetailsAffirmPaymentMethod';
import { OrderDetailsAdyenPaymentMethod } from './OrderDetailsAdyenPaymentMethod';
import { OrderDetailsPaymentMethodOptions } from './OrderDetailsPaymentMethodOptions';

enum Mode {
  Resting,
  Changing
}

export const OrderDetailsPaymentSection: React.FC = () => {
  const history = useHistory();
  const { orderSummary } = useOrderDetails();
  const { getHrefFor, params } = useUrlParams();
  const selectedPaymentMethodSource = orderSummary?.attributes.paymentMethod.source;

  const [mode, setMode] = useState(
    selectedPaymentMethodSource === PaymentSource.Amazon ||
      selectedPaymentMethodSource === PaymentSource.PayPal
      ? Mode.Changing
      : Mode.Resting
  );

  const paymentMethodRef = useRef<HTMLDivElement | null>(null);

  const handleScrollTo = useCallback(() => {
    const element = paymentMethodRef?.current;

    if (element) {
      const { offsetTop } = element;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  }, [paymentMethodRef]);

  const onModeChange = useCallback(
    (incomingMode: number) => {
      if (incomingMode !== Mode.Resting) {
        setMode(Mode.Changing);
        handleScrollTo();
        return;
      }

      setMode(Mode.Resting);
      history.replace(getHrefFor(omit(['state'], params)));
    },
    [params, history, getHrefFor, handleScrollTo]
  );

  if (!orderSummary) return null;

  const { isPaymentChangeable, affirmSecondChargeEnabled, paymentMethod } = orderSummary.attributes;

  const changeLink = isPaymentChangeable ? (
    <ControlLink
      onClick={() => (mode === Mode.Resting ? setMode(Mode.Changing) : setMode(Mode.Resting))}
    >
      {mode === Mode.Resting ? 'Change' : 'Cancel'}
    </ControlLink>
  ) : null;

  return (
    <div ref={paymentMethodRef}>
      <Stack space={3}>
        <Stack alignItems="center" direction="horizontal" justifyContent="space-between" space={4}>
          {mode === Mode.Changing && (
            <Stack space={2}>
              <Text treatment="h5">Change Payment Method for Open Items</Text>
              <Stack direction="horizontal" space={1} alignItems="center">
                <Text color="code-red">
                  <WarningIcon />
                </Text>
                <Text treatment="body1" color="code-red">
                  This change will apply to all open items on this order
                </Text>
              </Stack>
            </Stack>
          )}

          {mode === Mode.Resting && <Text treatment="h5">Payments</Text>}
        </Stack>

        <OrderDetailsPaymentMethodOptions orderSummary={orderSummary} />

        <Box className="OrderDetailsPaymentSection__box" borderless={mode === Mode.Changing}>
          {affirmSecondChargeEnabled && <OrderDetailsAffirmPaymentMethod />}

          {!affirmSecondChargeEnabled && mode === Mode.Changing && (
            <OrderDetailsAdyenPaymentMethod
              defaultMode={AdyenPaymentMethodSelectorMode.Selecting}
              onModeChange={onModeChange}
            />
          )}

          {!affirmSecondChargeEnabled && mode === Mode.Resting && (
            <>
              {paymentMethod.source === PaymentSource.Adyen && (
                <OrderDetailsAdyenPaymentMethod onModeChange={onModeChange} />
              )}

              {paymentMethod.source === PaymentSource.PayPal && (
                <PayPalPaymentMethodPreview>{changeLink}</PayPalPaymentMethodPreview>
              )}

              {paymentMethod.source === PaymentSource.Affirm && (
                <AffirmPaymentMethodPreview>{changeLink}</AffirmPaymentMethodPreview>
              )}

              {paymentMethod.source === PaymentSource.Apple && (
                <ApplePaymentMethodPreview>{changeLink}</ApplePaymentMethodPreview>
              )}
            </>
          )}
        </Box>
      </Stack>
    </div>
  );
};
